import React, { useState } from "react";
import {
  Button,
  ChevronDown,
  Search,
  X,
} from "../../transactions/Exercise/utils/ComponentUtils";
import { Input } from "../../../components/shared/InputField";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../transactions/Exercise/utils/Popover";
import { Select } from "../../../components/shared/Select";
import { NameAndId } from "../../../types/Transaction";
import { HStack, VStack } from "../../../components/utils";

export interface GenericFilterType {
  id: string;
  label: string;
  options: string[] | NameAndId[];
  type:
    | "multiCheckbox"
    | "multiCheckboxNameAndId"
    | "number"
    | "excludedPersonnel"
    | "addPersonnel";
  value: any;
  onChange: (selectedValues: any) => void;
}

interface GenericFilterBarProps {
  filters: GenericFilterType[];
  disableUpdate?: boolean;
  initialVisibleCount?: number;
}

const GenericFilterBar: React.FC<GenericFilterBarProps> = ({
  filters,
  disableUpdate = false,
  initialVisibleCount = 4,
}) => {
  const [showAllFilters, setShowAllFilters] = useState(!disableUpdate);

  return (
    <HStack className="gap-4 flex-wrap">
      {/* Render the first N filters */}
      {filters.slice(0, initialVisibleCount).map((filter, index) => (
        <HStack key={filter.id}>
          {filter.type === "multiCheckbox" && (
            <GenericMultiCheckBoxPopOver
              index={index}
              filter={{
                ...filter,
                options: filter.options as string[],
              }}
              value={filter.value}
              onChange={filter.onChange}
              disableUpdate={disableUpdate}
            />
          )}
          {filter.type === "multiCheckboxNameAndId" && (
            <GenericMultiCheckBoxNameAndIdPopOver
              index={index}
              filter={{
                ...filter,
                options: filter.options as NameAndId[],
              }}
              value={filter.value}
              onChange={filter.onChange}
              disableUpdate={disableUpdate}
            />
          )}
          {filter.type === "number" && (
            <GenericNumberFilterPopOver
              index={index}
              filter={filter}
              value={filter.value}
              onChange={filter.onChange}
              disableUpdate={disableUpdate}
            />
          )}
          {filter.type === "excludedPersonnel" && (
            <GenericExcludedPersonnel
              options={filter.options as { name: string; id: string }[]}
              value={filter.value}
              onChange={filter.onChange}
              disableUpdate={disableUpdate}
            />
          )}
          {filter.type === "addPersonnel" && (
            <GenericAddPersonnelFilter
              options={filter.options as { name: string; id: string }[]}
              value={filter.value}
              onChange={filter.onChange}
              disableUpdate={disableUpdate}
            />
          )}
        </HStack>
      ))}

      {/* Render additional filters if showAllFilters is true */}
      {showAllFilters &&
        filters.slice(initialVisibleCount).map((filter, index) => (
          <HStack key={filter.id}>
            {filter.type === "multiCheckbox" && (
              <GenericMultiCheckBoxPopOver
                index={index + initialVisibleCount}
                filter={{
                  ...filter,
                  options: filter.options as string[],
                }}
                value={filter.value}
                onChange={filter.onChange}
                disableUpdate={disableUpdate}
              />
            )}
            {filter.type === "multiCheckboxNameAndId" && (
              <GenericMultiCheckBoxNameAndIdPopOver
                index={index + initialVisibleCount}
                filter={{
                  ...filter,
                  options: filter.options as NameAndId[],
                }}
                value={filter.value}
                onChange={filter.onChange}
                disableUpdate={disableUpdate}
              />
            )}
            {filter.type === "number" && (
              <GenericNumberFilterPopOver
                index={index + initialVisibleCount}
                filter={filter}
                value={filter.value}
                onChange={filter.onChange}
                disableUpdate={disableUpdate}
              />
            )}
            {filter.type === "excludedPersonnel" && (
              <GenericExcludedPersonnel
                options={filter.options as { name: string; id: string }[]}
                value={filter.value}
                onChange={filter.onChange}
                disableUpdate={disableUpdate}
              />
            )}
            {filter.type === "addPersonnel" && (
              <GenericAddPersonnelFilter
                options={filter.options as { name: string; id: string }[]}
                value={filter.value}
                onChange={filter.onChange}
                disableUpdate={disableUpdate}
              />
            )}
          </HStack>
        ))}

      {/* Toggle button for showing/hiding additional filters */}
      {filters.length > initialVisibleCount && (
        <div className="flex items-center justify-end gap-2 cursor-pointer">
          <a
            onClick={() => setShowAllFilters((prev) => !prev)}
            className="items-center text-sm font-medium text-center cursor-pointer"
          >
            {!showAllFilters && `+${filters.length - initialVisibleCount} More`}
          </a>
        </div>
      )}
    </HStack>
  );
};

// --- GENERIC MULTI CHECKBOX POPOVER ---
export function GenericMultiCheckBoxPopOver({
  index,
  filter,
  value,
  onChange,
  disableUpdate = false,
}: {
  index: number;
  filter: { id: string; label: string; options: string[] };
  value: string[];
  onChange: (val: string[]) => void;
  disableUpdate?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [tempSelected, setTempSelected] = useState<string[]>(value || []);

  const isActive = Array.isArray(value) && value.length > 0;

  return (
    <Popover
      className="left-0"
      leftonly={true}
      key={index}
      content={
        <PopoverContent className="w-60">
          <VStack className="gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search"
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <VStack className="gap-2 overflow-y-auto max-h-60">
              {filter.options
                .filter((option) =>
                  option.toLowerCase().includes(search.toLowerCase())
                )
                .map((option, optIdx) => (
                  <HStack key={optIdx} className="justify-between">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={tempSelected.includes(option)}
                        onChange={(e) => {
                          if (disableUpdate) return;
                          const isChecked = e.target.checked;
                          setTempSelected((prev) =>
                            isChecked
                              ? [...prev, option]
                              : prev.filter((val) => val !== option)
                          );
                        }}
                      />
                      {option.replaceAll("_", " ")}
                    </label>
                  </HStack>
                ))}
            </VStack>
            {!disableUpdate && (
              <HStack className="justify-between pt-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setTempSelected([]);
                    onChange([]);
                  }}
                >
                  Clear
                </Button>
                <Button
                  className="text-white bg-orange-501 hover:bg-orange-600"
                  variant="primary"
                  onClick={() => onChange(tempSelected)}
                >
                  Apply
                </Button>
              </HStack>
            )}
          </VStack>
        </PopoverContent>
      }
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${
            isActive ? "bg-[#003366] text-white hover:bg-[#003366]" : ""
          }`}
        >
          {filter.label}
          {isActive && (
            <span className="bg-white text-[#003366] rounded-full w-6 h-6 flex items-center justify-center">
              {value.length}
            </span>
          )}
          {isActive && !disableUpdate ? (
            <X
              className="w-4 h-4 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setTempSelected([]);
                onChange([]);
              }}
            />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </PopoverTrigger>
    </Popover>
  );
}

// --- GENERIC MULTI CHECKBOX NAME AND ID POPOVER ---
export function GenericMultiCheckBoxNameAndIdPopOver({
  index,
  filter,
  value,
  onChange,
  disableUpdate = false,
}: {
  index: number;
  filter: { id: string; label: string; options: NameAndId[] };
  value: NameAndId[];
  onChange: (val: NameAndId[]) => void;
  disableUpdate?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [tempSelected, setTempSelected] = useState<NameAndId[]>(value || []);

  const isActive = Array.isArray(value) && value.length > 0;

  return (
    <Popover
      className="left-0"
      leftonly={true}
      key={index}
      content={
        <PopoverContent className="w-60">
          <VStack className="gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search"
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <VStack className="gap-2 overflow-y-auto max-h-60">
              {filter.options
                .filter((option) =>
                  option.name.toLowerCase().includes(search.toLowerCase())
                )
                .map((option, optIdx) => (
                  <HStack key={optIdx} className="justify-between">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={tempSelected.some(
                          (val) => val.id === option.id
                        )}
                        onChange={(e) => {
                          if (disableUpdate) return;
                          const isChecked = e.target.checked;
                          setTempSelected((prev) =>
                            isChecked
                              ? [...prev, option]
                              : prev.filter((val) => val.id !== option.id)
                          );
                        }}
                      />
                      {option.name.replaceAll("_", " ")}
                    </label>
                  </HStack>
                ))}
            </VStack>
            {!disableUpdate && (
              <HStack className="justify-between pt-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setTempSelected([]);
                    onChange([]);
                  }}
                >
                  Clear
                </Button>
                <Button
                  className="text-white bg-orange-501 hover:bg-orange-600"
                  variant="primary"
                  onClick={() => onChange(tempSelected)}
                >
                  Apply
                </Button>
              </HStack>
            )}
          </VStack>
        </PopoverContent>
      }
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${
            isActive ? "bg-[#003366] text-white hover:bg-[#003366]" : ""
          }`}
        >
          {filter.label}
          {isActive && (
            <span className="bg-white text-[#003366] rounded-full w-6 h-6 flex items-center justify-center">
              {value.length}
            </span>
          )}
          {isActive && !disableUpdate ? (
            <X
              className="w-4 h-4 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setTempSelected([]);
                onChange([]);
              }}
            />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </PopoverTrigger>
    </Popover>
  );
}

// --- GENERIC NUMBER FILTER POPOVER ---
export function GenericNumberFilterPopOver({
  index,
  filter,
  value,
  onChange,
  disableUpdate = false,
}: {
  index: number;
  filter: { id: string; label: string };
  value: { min?: number; max?: number };
  onChange: (val: { min?: number; max?: number }) => void;
  disableUpdate?: boolean;
}) {
  const [tempFilter, setTempFilter] = useState<{
    type: "Above" | "Below" | "Between";
    min?: number;
    max?: number;
  }>(() => {
    if (value) {
      if (value.min !== undefined && value.max !== undefined) {
        return { type: "Between", min: value.min, max: value.max };
      } else if (value.min !== undefined) {
        return { type: "Above", min: value.min };
      } else if (value.max !== undefined) {
        return { type: "Below", max: value.max };
      }
    }
    return { type: "Above", min: undefined, max: undefined };
  });

  const isActive =
    (value?.min !== undefined && value.min !== null) ||
    (value?.max !== undefined && value.max !== null);

  return (
    <Popover
      className="left-0"
      leftonly={true}
      key={index}
      content={
        <PopoverContent className="w-60">
          <VStack className="gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Filter Type
              </label>
              <Select
                className="w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-orange-501 focus:border-orange-501"
                value={tempFilter.type}
                onChange={(e) => {
                  if (disableUpdate) return;

                  setTempFilter((prev) => ({
                    ...prev,
                    type: e.target.value as "Above" | "Below" | "Between",
                    max: e.target.value === "Between" ? prev.max : undefined,
                  }));
                }}
                options={[
                  { value: "Above", label: "Above" },
                  { value: "Below", label: "Below" },
                  { value: "Between", label: "Between" },
                ]}
                textGetter={(option) => option.label}
                valueGetter={(option) => option.value}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {tempFilter.type === "Between" ? "Min" : "Value"}
              </label>
              <Input
                type="number"
                className="w-full mt-1"
                value={
                  tempFilter.type === "Below"
                    ? tempFilter.max || ""
                    : tempFilter.min || ""
                }
                onChange={(e) => {
                  if (disableUpdate) return;
                  const value = parseInt(e.target.value, 10) || undefined;
                  setTempFilter((prev) => ({
                    ...prev,
                    ...(tempFilter.type === "Below"
                      ? { max: value }
                      : { min: value }),
                  }));
                }}
              />
            </div>
            {tempFilter.type === "Between" && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Max
                </label>
                <Input
                  type="number"
                  className="w-full mt-1"
                  value={tempFilter.max || ""}
                  onChange={(e) => {
                    if (disableUpdate) return;
                    const value = parseInt(e.target.value, 10) || undefined;
                    setTempFilter((prev) => ({
                      ...prev,
                      max: value,
                    }));
                  }}
                />
              </div>
            )}
            {!disableUpdate && (
              <HStack className="justify-between pt-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setTempFilter({
                      type: "Above",
                      min: undefined,
                      max: undefined,
                    });
                    onChange({});
                  }}
                >
                  Clear
                </Button>
                <Button
                  className="text-white bg-orange-501 hover:bg-orange-600"
                  variant="primary"
                  onClick={() => {
                    onChange({
                      min:
                        tempFilter.type === "Below"
                          ? undefined
                          : tempFilter.min,
                      max:
                        tempFilter.type === "Above"
                          ? undefined
                          : tempFilter.max,
                    });
                  }}
                >
                  Apply
                </Button>
              </HStack>
            )}
          </VStack>
        </PopoverContent>
      }
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${
            isActive ? "bg-[#003366] text-white hover:bg-[#003366]" : ""
          }`}
        >
          {filter.label}
          {isActive && (
            <span>
              {value.min !== undefined && value.max !== undefined
                ? `> ${value.min} & < ${value.max}`
                : value.min !== undefined
                ? `> ${value.min}`
                : value.max !== undefined
                ? `< ${value.max}`
                : ""}
            </span>
          )}
          {isActive && !disableUpdate ? (
            <X
              className="w-4 h-4 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setTempFilter({
                  type: "Above",
                  min: undefined,
                  max: undefined,
                });
                onChange({});
              }}
            />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </PopoverTrigger>
    </Popover>
  );
}

// --- GENERIC EXCLUDED PERSONNEL ---
export function GenericExcludedPersonnel({
  options,
  value,
  onChange,
  disableUpdate = false,
}: {
  options: { name: string; id: string }[];
  value: { name: string; id: string }[];
  onChange: (val: { name: string; id: string }[]) => void;
  disableUpdate?: boolean;
}) {
  const [searchValue, setSearchValue] = useState<string>("");

  let filteredOptions = options.filter((option) =>
    option.name.toLowerCase()?.includes(searchValue.toLowerCase())
  );

  const isExcluded = (id: string) =>
    value?.map((e: any) => e.id || e.employeeId).includes(id);

  if (disableUpdate) {
    filteredOptions = filteredOptions.filter((option) => isExcluded(option.id));
  }

  const toggleExcludeEmployee = (option: { name: string; id: string }) => {
    if (disableUpdate) return;
    const updatedIds = isExcluded(option.id)
      ? value.filter(
          (excludeId: { name: string; id: string }) =>
            excludeId.id !== option.id
        )
      : [...value, option];
    onChange(updatedIds);
  };

  return (
    <Popover
      className="left-0"
      leftonly={true}
      content={
        <PopoverContent className="w-80">
          <VStack className="gap-3">
            <div className="text-lg font-medium">Excluded Personnel</div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search by name"
                className="pl-10"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
            <VStack className="gap-2 overflow-y-auto max-h-60">
              {filteredOptions.map((option) => (
                <HStack
                  key={option.id}
                  className="justify-between px-3 py-2 border rounded-md"
                >
                  <span>{option.name}</span>
                  {isExcluded(option.id) ? (
                    <button
                      className="text-gray-500"
                      onClick={() => toggleExcludeEmployee(option)}
                    >
                      ✕
                    </button>
                  ) : (
                    <button
                      className="text-blue-500"
                      onClick={() => toggleExcludeEmployee(option)}
                    >
                      Exclude
                    </button>
                  )}
                </HStack>
              ))}
            </VStack>
            {!disableUpdate && (
              <HStack className="justify-between pt-3">
                <Button variant="outline" onClick={() => onChange([])}>
                  Clear All
                </Button>
                <Button
                  variant="primary"
                  className="text-white bg-orange-501 hover:bg-orange-600"
                  onClick={() => {}}
                >
                  Apply
                </Button>
              </HStack>
            )}
          </VStack>
        </PopoverContent>
      }
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${
            value?.length > 0 ? "bg-[#003366] text-white" : ""
          }`}
          disabled={disableUpdate}
        >
          Excluded Personnel
          {value?.length > 0 && (
            <span className="bg-white text-[#003366] rounded-full w-6 h-6 flex items-center justify-center">
              {value?.length}
            </span>
          )}
          {value?.length > 0 && !disableUpdate ? (
            <X
              className="w-4 h-4 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </PopoverTrigger>
    </Popover>
  );
}

// --- GENERIC ADD PERSONNEL ---
export function GenericAddPersonnelFilter({
  options,
  value,
  onChange,
  disableUpdate = false,
}: {
  options: { name: string; id: string }[];
  value: { name: string; id: string }[];
  onChange: (val: { name: string; id: string }[]) => void;
  disableUpdate?: boolean;
}) {
  const [searchValue, setSearchValue] = useState<string>("");

  let filteredOptions = options.filter((option) =>
    option.name.toLowerCase()?.includes(searchValue.toLowerCase())
  );

  const isAdded = (id: string) =>
    value?.map((e: any) => e.id || e.employeeId).includes(id);

  if (disableUpdate) {
    filteredOptions = filteredOptions.filter((option) => isAdded(option.id));
  }

  const toggleAddEmployee = (option: { name: string; id: string }) => {
    if (disableUpdate) return;
    const updatedIds = isAdded(option.id)
      ? value.filter(
          (includeId: { name: string; id: string }) =>
            includeId.id !== option.id
        )
      : [...value, option];
    onChange(updatedIds);
  };

  return (
    <Popover
      className="left-0"
      leftonly={true}
      content={
        <PopoverContent className="w-80">
          <VStack className="gap-3">
            <div className="text-lg font-medium">Include Personnel</div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search by name"
                className="pl-10"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
            <VStack className="gap-2 overflow-y-auto max-h-60">
              {filteredOptions.map((option) => (
                <HStack
                  key={option.id}
                  className="justify-between px-3 py-2 border rounded-md"
                >
                  <span>{option.name}</span>
                  {isAdded(option.id) ? (
                    <button
                      className="text-gray-500"
                      onClick={() => toggleAddEmployee(option)}
                    >
                      ✕
                    </button>
                  ) : (
                    <button
                      className="text-blue-500"
                      onClick={() => toggleAddEmployee(option)}
                    >
                      Add
                    </button>
                  )}
                </HStack>
              ))}
            </VStack>
            {!disableUpdate && (
              <HStack className="justify-between pt-3">
                <Button variant="outline" onClick={() => onChange([])}>
                  Clear All
                </Button>
                <Button
                  variant="primary"
                  className="text-white bg-orange-501 hover:bg-orange-600"
                  onClick={() => {}}
                >
                  Apply
                </Button>
              </HStack>
            )}
          </VStack>
        </PopoverContent>
      }
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${
            value?.length > 0 ? "bg-[#003366] text-white" : ""
          }`}
          disabled={disableUpdate}
        >
          Include Personnel
          {value?.length > 0 && (
            <span className="bg-white text-[#003366] rounded-full w-6 h-6 flex items-center justify-center">
              {value?.length}
            </span>
          )}
          {value?.length > 0 && !disableUpdate ? (
            <X
              className="w-4 h-4 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </PopoverTrigger>
    </Popover>
  );
}

export default GenericFilterBar;
